import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { Tenant, AgentStatus, AdminUser } from '@/types';

export function useTenants(enabled = true) {
  return useQuery({
    queryKey: ['tenants'],
    queryFn: async () => {
      const { data } = await apiClient.get<Tenant[]>('/admin/tenants');
      return data;
    },
    enabled,
  });
}

export interface SaveAdminUserInput {
  id?: string;
  username: string;
  password: string;
  role: 'admin' | 'user';
  active: boolean;
  tenantIds: string[];
}

export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const { data } = await apiClient.get<AdminUser[]>('/admin/users');
      return data;
    },
  });
}

export function useSaveAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: SaveAdminUserInput) => {
      const request = id
        ? apiClient.put<AdminUser>(`/admin/users/${id}`, input)
        : apiClient.post<AdminUser>('/admin/users', input);
      return (await request).data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  });
}

export function useTenant(id: string) {
  return useQuery({
    queryKey: ['tenant', id],
    queryFn: async () => {
      const { data } = await apiClient.get<Tenant>(`/admin/tenants/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateTenant() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (tenant: Omit<Tenant, 'id' | 'createdAt'>) => {
      const { data } = await apiClient.post<Tenant>('/admin/tenants', tenant);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
}

export function useUpdateTenant() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...tenant }: Partial<Tenant> & { id: string }) => {
      const { data } = await apiClient.put<Tenant>(`/admin/tenants/${id}`, tenant);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
}

export function useDeleteTenant() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/admin/tenants/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
}

export function useAgentStatus() {
  return useQuery({
    queryKey: ['agent-status'],
    queryFn: async () => {
      const { data } = await apiClient.get<AgentStatus[]>('/admin/agents/status');
      return data;
    },
    refetchInterval: 30000,
  });
}

export interface DashboardSetting {
  tenantId: string;
  tenantName: string;
  unreturnedUnitCostUsd: number;
  currency: 'USD' | 'EUR';
  updatedAt: string | null;
}

export function useDashboardSettings() {
  return useQuery({
    queryKey: ['dashboard-settings'],
    queryFn: async () => {
      const { data } = await apiClient.get<DashboardSetting[]>('/admin/dashboard-settings');
      return data;
    },
  });
}

export function useUpdateDashboardSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId, unreturnedUnitCostUsd, currency }: {
      tenantId: string;
      unreturnedUnitCostUsd: number;
      currency: 'USD' | 'EUR';
    }) => {
      const { data } = await apiClient.put<DashboardSetting>(
        `/admin/dashboard-settings/${tenantId}`,
        { unreturnedUnitCostUsd, currency },
      );
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData<DashboardSetting[]>(['dashboard-settings'], current => current?.map(item => item.tenantId === data.tenantId ? {...item,...data} : item));
      queryClient.invalidateQueries({ queryKey: ['product-costs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-settings'] });
      queryClient.invalidateQueries({ queryKey: ['returns-analytics'] });
    },
  });
}

export interface ProductCost {
  articleCode: string;
  articleName: string;
  configuredUnitCostUsd: number | null;
  effectiveUnitCostUsd: number;
}

export function useProductCosts(tenantId: string) {
  return useQuery({
    queryKey: ['product-costs', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get<ProductCost[]>(`/admin/product-costs/${tenantId}`);
      return data;
    },
    enabled: !!tenantId,
  });
}

export function useUpdateProductCosts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId, costs }: {
      tenantId: string;
      costs: Array<{ articleCode: string; unitCostUsd: number }>;
    }) => {
      const { data } = await apiClient.put<{ tenantId: string; updated: number }>(
        `/admin/product-costs/${tenantId}`,
        { costs },
      );
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['product-costs', variables.tenantId] });
      queryClient.invalidateQueries({ queryKey: ['returns-analytics'] });
    },
  });
}
